import { useState, useEffect } from "react";
import type { SlotAvailability, SlotBookingInfo } from "../lib/types";
import { RESOURCE_LABELS, DAY_NAMES } from "../lib/types";
import {
  adminToggleSlot,
  adminCancelBooking,
  adminCancelBookingGroup,
  adminDeleteWorkshop,
  adminGetSlotBookings,
  adminUpdateSlotCapacity,
  adminSetClayground,
  adminRegeneratePIN,
  adminSetManualPIN,
} from "../lib/hooks";
import { Modal } from "./Modal";

interface AdminSlotPopupProps {
  slot: SlotAvailability;
  onClose: () => void;
  onRefetch: () => void;
}

export function AdminSlotPopup({ slot, onClose, onRefetch }: AdminSlotPopupProps) {
  const [bookings, setBookings] = useState<SlotBookingInfo[]>([]);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [actionLoading, setActionLoading] = useState("");
  const [error, setError] = useState("");
  const [confirmDeleteWorkshop, setConfirmDeleteWorkshop] = useState(false);
  const [confirmCancelGroup, setConfirmCancelGroup] = useState<string | null>(null);
  const [editingCapacity, setEditingCapacity] = useState(false);
  const [newCapacity, setNewCapacity] = useState(slot.capacity);
  const [capacitySuccess, setCapacitySuccess] = useState("");
  const [manualPINGroupId, setManualPINGroupId] = useState<string | null>(null);
  const [manualPINInput, setManualPINInput] = useState("");

  const isBlocked = slot.is_active === false;
  const isWorkshop = slot.resource_type === "workshop";

  const date = new Date(slot.date + "T00:00:00");
  const dayName = DAY_NAMES[date.getDay()];
  const dateStr = date.toLocaleDateString("sk-SK", {
    day: "numeric",
    month: "numeric",
    year: "numeric",
  });
  const timeStart = slot.start_time.slice(0, 5);
  const timeEnd = slot.end_time.slice(0, 5);
  const label = slot.title || RESOURCE_LABELS[slot.resource_type];
  const price = (slot.price_cents / 100).toFixed(2).replace(".", ",");

  useEffect(() => {
    loadBookings();
  }, [slot.slot_id]);

  const loadBookings = async () => {
    setLoadingBookings(true);
    const { data } = await adminGetSlotBookings(slot.slot_id);
    setBookings(data ?? []);
    setLoadingBookings(false);
  };

  const hasBookings = bookings.length > 0;

  const handleToggle = async () => {
    setActionLoading("toggle");
    setError("");
    const { error: err } = await adminToggleSlot(slot.slot_id, !slot.is_active);
    if (err) {
      setError(err.message);
    } else {
      onRefetch();
      onClose();
    }
    setActionLoading("");
  };

  const handleCancelBooking = async (bookingId: number) => {
    setActionLoading(`cancel-${bookingId}`);
    setError("");
    const { error: err } = await adminCancelBooking(bookingId);
    if (err) {
      setError(err.message);
    } else {
      await loadBookings();
      onRefetch();
    }
    setActionLoading("");
  };

  const handleCancelGroup = async (groupId: string) => {
    if (confirmCancelGroup !== groupId) {
      setConfirmCancelGroup(groupId);
      return;
    }
    setActionLoading(`cancel-group-${groupId}`);
    setError("");
    const { error: err } = await adminCancelBookingGroup(groupId);
    if (err) {
      setError(err.message);
    } else {
      setConfirmCancelGroup(null);
      await loadBookings();
      onRefetch();
    }
    setActionLoading("");
  };

  const handleRegeneratePIN = async (groupId: string) => {
    setActionLoading(`pin-regen-${groupId}`);
    setError("");
    const { error: err } = await adminRegeneratePIN(groupId);
    if (err) {
      setError("Generovanie PIN zlyhalo: " + err.message);
    } else {
      await loadBookings();
    }
    setActionLoading("");
  };

  const handleManualPINSubmit = async () => {
    if (!manualPINGroupId || !manualPINInput.trim()) return;
    setActionLoading(`pin-manual-${manualPINGroupId}`);
    setError("");
    const { error: err } = await adminSetManualPIN(manualPINGroupId, manualPINInput.trim());
    if (err) {
      setError("Uloženie PIN zlyhalo: " + err.message);
    } else {
      setManualPINGroupId(null);
      setManualPINInput("");
      await loadBookings();
    }
    setActionLoading("");
  };

  const totalPersons = bookings.reduce((sum, b) => sum + (b.num_persons ?? 1), 0);

  // Clayground: one click marks both stôl and kruh of this hour.
  const handleClayground = async () => {
    setActionLoading("clayground");
    setError("");
    const { error: err } = await adminSetClayground(slot.slot_id, !slot.clayground);
    if (err) {
      setError("Clayground sa nepodarilo zmeniť: " + err.message);
    } else {
      onRefetch();
      onClose();
    }
    setActionLoading("");
  };

  const handleUpdateCapacity = async () => {
    setActionLoading("capacity");
    setError("");
    setCapacitySuccess("");
    const { error: err } = await adminUpdateSlotCapacity(slot.slot_id, newCapacity);
    if (err) {
      setError(err.message);
    } else {
      setEditingCapacity(false);
      setCapacitySuccess(`Kapacita zmenená na ${newCapacity}`);
      onRefetch();
    }
    setActionLoading("");
  };

  const handleDeleteWorkshop = async () => {
    if (bookings.length > 0 && !confirmDeleteWorkshop) {
      setConfirmDeleteWorkshop(true);
      return;
    }
    setActionLoading("delete");
    setError("");
    const { error: err } = await adminDeleteWorkshop(slot.slot_id);
    if (err) {
      setError(err.message);
    } else {
      onRefetch();
      onClose();
    }
    setActionLoading("");
    setConfirmDeleteWorkshop(false);
  };

  return (
    <Modal>
      <div className="popup-overlay" onClick={onClose}>
        <div className="popup admin-popup" onClick={(e) => e.stopPropagation()}>
          <div className="popup-head">
            <span className="popup-eyebrow">
              Admin · {isWorkshop ? "Workshop" : "Slot"}
              {isBlocked && <span className="blocked-tag">Zablokované</span>}
              {slot.clayground && <span className="clayground-tag">Clayground</span>}
            </span>
            <h3>{label}</h3>
            <span className="popup-sub">{dayName}, {dateStr}</span>
          </div>

          <dl className="popup-grid">
            <dt>Čas</dt>
            <dd>{timeStart} – {timeEnd}</dd>
            {!isBlocked && (
              <>
                <dt>Cena</dt>
                <dd>{price} € / os.</dd>
                <dt>Kapacita</dt>
                <dd>
                  {editingCapacity ? (
                    <span className="capacity-edit">
                      <span>{slot.booked} z</span>
                      <input
                        type="number"
                        className="capacity-input"
                        value={newCapacity}
                        onChange={(e) => setNewCapacity(Math.max(1, parseInt(e.target.value) || 1))}
                        min={slot.booked || 1}
                      />
                      <button
                        className="btn btn-small btn-primary"
                        onClick={handleUpdateCapacity}
                        disabled={actionLoading === "capacity" || newCapacity === slot.capacity}
                      >
                        {actionLoading === "capacity" ? "..." : "Uložiť"}
                      </button>
                      <button
                        className="btn btn-small btn-secondary"
                        onClick={() => { setEditingCapacity(false); setNewCapacity(slot.capacity); }}
                      >
                        Zrušiť
                      </button>
                    </span>
                  ) : (
                    <span className="popup-occupancy">
                      <span className="popup-bar" aria-hidden="true">
                        <span className="popup-bar-fill" style={{ width: `${slot.capacity > 0 ? Math.round((slot.booked / slot.capacity) * 100) : 0}%` }}></span>
                      </span>
                      <span>{slot.booked} z {slot.capacity}</span>
                      <button
                        className="btn btn-small btn-outline capacity-edit-btn"
                        onClick={() => setEditingCapacity(true)}
                      >
                        Zmeniť
                      </button>
                    </span>
                  )}
                </dd>
              </>
            )}
          </dl>

          {error && <div className="form-error">{error}</div>}
          {capacitySuccess && <div className="form-success">{capacitySuccess}</div>}

          {/* Block hint when bookings exist */}
          {!isBlocked && hasBookings && !loadingBookings && (
            <div className="admin-info-hint">
              Najprv zrušte všetky rezervácie pred zablokovaním slotu.
            </div>
          )}

          {/* Workshop delete confirmation warning */}
          {confirmDeleteWorkshop && (
            <div className="admin-confirm-warning">
              Workshop má {totalPersons} osôb v {bookings.length} rezerváciách. Všetky budú zrušené a kredit vrátený ({((slot.price_cents * totalPersons) / 100).toFixed(2).replace(".", ",")} €).
              <div className="admin-confirm-actions">
                <button className="btn btn-small btn-danger" onClick={handleDeleteWorkshop} disabled={actionLoading === "delete"}>
                  {actionLoading === "delete" ? "..." : "Áno, zmazať a vrátiť kredit"}
                </button>
                <button className="btn btn-small btn-secondary" onClick={() => setConfirmDeleteWorkshop(false)}>
                  Zrušiť
                </button>
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="popup-actions">
            <button className="btn btn-secondary" onClick={onClose}>
              Zavrieť
            </button>
            <>
                {isBlocked ? (
                  <button
                    className="btn btn-primary"
                    onClick={handleToggle}
                    disabled={actionLoading === "toggle"}
                  >
                    {actionLoading === "toggle" ? "..." : "Odblokovať"}
                  </button>
                ) : (
                  <button
                    className="btn btn-danger"
                    onClick={handleToggle}
                    disabled={actionLoading === "toggle" || hasBookings}
                  >
                    {actionLoading === "toggle" ? "..." : "Zablokovať"}
                  </button>
                )}
                {!isWorkshop && !isBlocked && (
                  <button
                    className={`btn ${slot.clayground ? "btn-secondary" : "btn-clayground"}`}
                    onClick={handleClayground}
                    disabled={actionLoading === "clayground"}
                    title="Označí stôl aj kruh v tejto hodine"
                  >
                    {actionLoading === "clayground" ? "..." : slot.clayground ? "Zrušiť Clayground" : "Clayground: som tu"}
                  </button>
                )}
                {isWorkshop && !confirmDeleteWorkshop && (
                  <button
                    className="btn btn-danger"
                    onClick={handleDeleteWorkshop}
                    disabled={actionLoading === "delete"}
                  >
                    {actionLoading === "delete" ? "..." : "Zmazať workshop"}
                  </button>
                )}
              </>
          </div>

          {/* Booking list — admin only */}
          {!isBlocked && (
            <div className="admin-bookings-section">
              <h4>Bookings ({bookings.length} rez., {bookings.reduce((sum, b) => sum + (b.num_persons ?? 1), 0)} osôb)</h4>
              {loadingBookings ? (
                <div className="loading-small">Načítavam...</div>
              ) : bookings.length === 0 ? (
                <p className="empty">Žiadne bookings.</p>
              ) : (
                <div className="admin-booking-list">
                  {bookings.map((b) => {
                    const isMultiHour = b.group_slot_count > 1;
                    // Unpaid (customer has not finished Stripe): the API cancels the whole
                    // group and returns only deducted credit, so one button is enough.
                    const isUnpaid = b.status === "pending_payment";
                    const groupLoading = actionLoading === `cancel-group-${b.booking_group_id}`;
                    return (
                      <div key={b.id} className="admin-booking-item">
                        <div className="admin-booking-info">
                          <span className="admin-booking-name">{b.name}</span>
                          <span className="admin-booking-email">{b.email}</span>
                          <span className="admin-booking-code">{b.customer_code}</span>
                          {(b.num_persons ?? 1) > 1 && (
                            <span className="admin-booking-persons">{b.num_persons} osôb</span>
                          )}
                          {isMultiHour && (
                            <span className="admin-booking-group-badge">{b.group_slot_count}h booking</span>
                          )}
                          {b.discount_code && (
                            <span className="admin-booking-group-badge">Zľava {b.discount_code}</span>
                          )}
                          {isUnpaid && (
                            <span className="pin-badge pin-unpaid">Čaká na platbu</span>
                          )}
                          {b.status === "confirmed" && (
                            b.pin_status === "active" ? (
                              <span className="pin-badge pin-active">{b.pin ? `PIN ${b.pin}` : "PIN aktívny"}</span>
                            ) : (
                              <span className="pin-badge pin-missing">PIN chýba</span>
                            )
                          )}
                        </div>
                        {b.status === "confirmed" && b.pin_status !== "active" && (
                          <div className="admin-booking-actions">
                            <button
                              className="btn btn-small"
                              onClick={() => handleRegeneratePIN(b.booking_group_id)}
                              disabled={actionLoading === `pin-regen-${b.booking_group_id}`}
                            >
                              {actionLoading === `pin-regen-${b.booking_group_id}` ? "..." : "Generovať PIN"}
                            </button>
                            <button
                              className="btn btn-small btn-outline"
                              onClick={() => { setManualPINGroupId(b.booking_group_id); setManualPINInput(""); }}
                            >
                              Zadať PIN ručne
                            </button>
                          </div>
                        )}
                        {manualPINGroupId === b.booking_group_id && (
                          <div className="admin-confirm-warning">
                            <p style={{ margin: "0 0 8px 0" }}>Zadaj PIN z Tedee aplikácie (max 10 znakov):</p>
                            <input
                              type="text"
                              className="form-input"
                              value={manualPINInput}
                              onChange={(e) => setManualPINInput(e.target.value)}
                              maxLength={10}
                              placeholder="napr. 482913"
                              autoFocus
                            />
                            <div className="admin-confirm-actions" style={{ marginTop: 8 }}>
                              <button
                                className="btn btn-small btn-primary"
                                onClick={handleManualPINSubmit}
                                disabled={!manualPINInput.trim() || actionLoading === `pin-manual-${b.booking_group_id}`}
                              >
                                {actionLoading === `pin-manual-${b.booking_group_id}` ? "..." : "Uložiť"}
                              </button>
                              <button
                                className="btn btn-small btn-secondary"
                                onClick={() => { setManualPINGroupId(null); setManualPINInput(""); }}
                              >
                                Zrušiť
                              </button>
                            </div>
                          </div>
                        )}
                        {confirmCancelGroup === b.booking_group_id && (
                          <div className="admin-confirm-warning">
                            Booking má {b.group_slot_count} slotov. Všetky budú zrušené a kredit vrátený.
                            <div className="admin-confirm-actions">
                              <button className="btn btn-small btn-danger" onClick={() => handleCancelGroup(b.booking_group_id)} disabled={groupLoading}>
                                {groupLoading ? "..." : "Áno, zrušiť celý booking"}
                              </button>
                              <button className="btn btn-small btn-secondary" onClick={() => setConfirmCancelGroup(null)}>
                                Zrušiť
                              </button>
                            </div>
                          </div>
                        )}
                        <div className="admin-booking-actions">
                          <button
                            className="btn btn-small btn-danger"
                            onClick={() => handleCancelBooking(b.id)}
                            disabled={actionLoading === `cancel-${b.id}`}
                          >
                            {actionLoading === `cancel-${b.id}` ? "..." : isUnpaid ? "Zrušiť rezerváciu" : isMultiHour ? "Zrušiť slot" : "Zrušiť"}
                          </button>
                          {isMultiHour && !isUnpaid && (
                            <button
                              className="btn btn-small btn-danger"
                              onClick={() => handleCancelGroup(b.booking_group_id)}
                              disabled={groupLoading}
                            >
                              {groupLoading ? "..." : `Zrušiť všetky sloty (${b.group_slot_count})`}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
